import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import type { ComponentProps } from 'react';

import { cn } from './cn';
import { Spinner } from './spinner';

export const buttonVariants = cva(
  [
    'relative inline-flex shrink-0 items-center justify-center gap-2 rounded-control border border-transparent',
    'font-sans font-medium leading-none whitespace-nowrap select-none',
    'transition-[background-color,border-color,color] duration-(--lume-dur-instant) ease-out',
    'disabled:cursor-not-allowed disabled:opacity-45 aria-disabled:cursor-not-allowed aria-disabled:opacity-45',
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-icon",
  ],
  {
    variants: {
      variant: {
        primary:
          'bg-primary text-primary-foreground enabled:hover:bg-primary-hover enabled:active:bg-primary-press',
        secondary:
          'border-input bg-card text-foreground shadow-1 enabled:hover:bg-muted disabled:shadow-none',
        ghost: 'bg-transparent text-foreground enabled:hover:bg-muted',
        link: 'h-auto! bg-transparent px-0! text-accent-text underline-offset-4 enabled:hover:underline',
        danger: 'bg-err-wash text-err enabled:hover:bg-err-wash/80',
        'danger-solid':
          'bg-destructive text-destructive-foreground enabled:hover:bg-destructive-hover',
      },
      size: {
        sm: 'h-control-sm px-2.5 text-[calc(var(--lume-ctl-fs)-1px)]',
        default: 'h-control px-control-x text-control',
        lg: 'h-control-lg px-[calc(var(--lume-ctl-px)+6px)] text-[calc(var(--lume-ctl-fs)+1px)]',
        icon: 'size-control p-0',
      },
      block: { true: 'w-full' },
    },
    defaultVariants: { variant: 'primary', size: 'default' },
  },
);

export type ButtonProps = ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    readonly asChild?: boolean;
    /** Mostra o spinner, bloqueia o clique e marca `aria-busy`. O rótulo continua lá. */
    readonly loading?: boolean;
  };

/** Botão do Lume. Ação primária é uma por região; destrutiva confirmada usa `danger-solid`. */
export function Button({
  className,
  variant,
  size,
  block,
  asChild = false,
  loading = false,
  disabled,
  type,
  children,
  ...props
}: ButtonProps) {
  const Component = asChild ? Slot.Root : 'button';
  return (
    <Component
      data-slot="button"
      data-variant={variant ?? 'primary'}
      type={asChild ? undefined : (type ?? 'button')}
      disabled={asChild ? undefined : disabled === true || loading}
      aria-busy={loading || undefined}
      className={cn(buttonVariants({ variant, size, block }), className)}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {loading ? <Spinner label={null} className="size-[1em]" /> : null}
          {children}
        </>
      )}
    </Component>
  );
}
